import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import {
  CodingStrategyEnum,
  OrientationEnum,
  PaperFormatEnum,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useEditableTrackableData } from '../../../hooks/useEditableTrackableData.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import type { DocumentTemplatePage_OneDocumentTemplate } from '../../../pages/DocumentTemplate/hooks/useFindOneDocumentTemplateQuery.hook';

export const useInitialValuesFactory = (
  pInitialValues?: DocumentTemplatePage_OneDocumentTemplate,
) => {
  const { generateId } = useIdFactory();

  const { trackableData } = useEditableTrackableData();

  const referencesContext = useReferencesContext();

  const { defaultDocumentType, defaultGenericStatus } = ensureDefined({
    defaultDocumentType: referencesContext.defaultDocumentType,
    defaultGenericStatus: referencesContext.defaultGenericStatus,
  });

  const { codingStrategiesCodeMap, contentOriginsCodeMap, printStylesCodeMap } =
    referencesContext;

  const initialValues: DocumentTemplatePage_OneDocumentTemplate = useMemo(
    () => ({
      __typename: 'DocumentTemplate' as const,
      _id: generateId(),
      code: EMPTY_STRING,
      label: EMPTY_STRING,
      statusId: defaultGenericStatus._id,
      ...trackableData,
      ...omitBy(pInitialValues, isNil),
      codingConfig: {
        __typename: CodingStrategyEnum.CodingConfigManual as const,
        _id: generateId(),
        strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
        code: EMPTY_STRING,
        kind: CodingStrategyEnum.CodingConfigManual,
        ...omitBy(pInitialValues?.codingConfig, isNil),
      },
      document: {
        __typename: 'Document' as const,
        _id: generateId(),
        code: EMPTY_STRING,
        typeId: defaultDocumentType._id,
        kind: defaultDocumentType.code,
        title: EMPTY_STRING,
        ...trackableData,
        ...omitBy(pInitialValues?.document, isNil),
        virtualRoot: {
          __typename: 'Section' as const,
          _id: generateId(),
          isVirtual: true,
          contentOriginId: contentOriginsCodeMap.Special._id,
          subSections: EMPTY_ARRAY,
          orientation: OrientationEnum.Portrait,
          paperFormat: PaperFormatEnum.A4,
          hasPagebreakAfter: false,
          hasPagebreakBefore: false,
          ...omitBy(pInitialValues?.document.virtualRoot, isNil),
        },
        codingConfig: {
          __typename: CodingStrategyEnum.CodingConfigManual as const,
          _id: generateId(),
          strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
          code: EMPTY_STRING,
          kind: CodingStrategyEnum.CodingConfigManual,
          ...omitBy(pInitialValues?.document.codingConfig, isNil),
        },
      },
      printing: {
        __typename: 'DocumentTemplatePrinting' as const,
        _id: generateId(),
        pdfPrints: EMPTY_ARRAY,
        pdfPrintsIncrement: 0,
        ...omitBy(pInitialValues?.printing, isNil),
        printSettings: {
          __typename: 'PrintSettings' as const,
          _id: generateId(),
          addFooters: true,
          addHeaders: true,
          printStyleId: printStylesCodeMap.Blank._id,
          ...omitBy(pInitialValues?.printing.printSettings, isNil),
        },
      },
    }),
    [
      codingStrategiesCodeMap.CodingConfigManual._id,
      contentOriginsCodeMap.Special._id,
      defaultDocumentType._id,
      defaultDocumentType.code,
      defaultGenericStatus._id,
      pInitialValues,
      printStylesCodeMap.Blank._id,
      generateId,
      trackableData,
    ],
  );

  return { initialValues };
};
